/**
 * hk_os · <VoiceRecorderButton>
 * ────────────────────────────────────────────────────────────────────
 * Large hold-to-record button (48×48 minimum, comfortably 96×96 by
 * default). Emits `onStart`, `onStop(blob)`, `onCancel`. Consumer owns
 * the media pipeline call (POST /api/oa/v1/media/upload with
 * kind=voice_note); this primitive is UI + recording only.
 *
 * Fallback: if `navigator.mediaDevices.getUserMedia` is unavailable, the
 * button appears disabled with a hint.
 *
 * Props:
 *   promptKey     — snippet key to play on press (default VOICE_RECORD_NOTE)
 *   maxSeconds    — auto-stop cap (default 30)
 *   onStart       — fires when recording begins
 *   onStop(blob)  — fires when recording stops (Blob or null on cancel)
 *   disabled
 *   testid
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Square } from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";

export function VoiceRecorderButton({
  promptKey = "VOICE_RECORD_NOTE",
  maxSeconds = 30,
  onStart,
  onStop,
  disabled = false,
  testid,
}) {
  const { getSnippet, play } = useVoiceRegistry();
  const snippet = getSnippet(promptKey);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const supported = typeof navigator !== "undefined" &&
                    navigator.mediaDevices &&
                    typeof window !== "undefined" &&
                    typeof window.MediaRecorder !== "undefined";

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setElapsed(0);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (disabled || recording || !supported) return;
    play(promptKey).catch(() => {});
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new window.MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        onStop?.(blob);
        cleanup();
      };
      rec.start();
      mediaRef.current = rec;
      setRecording(true);
      setElapsed(0);
      onStart?.();
      timerRef.current = setInterval(() => {
        setElapsed((e) => {
          if (e + 1 >= maxSeconds) {
            try { rec.stop(); } catch { /* noop */ }
          }
          return e + 1;
        });
      }, 1000);
    } catch {
      cleanup();
      onStop?.(null);
    }
  }, [disabled, recording, supported, play, promptKey, onStart, onStop, maxSeconds, cleanup]);

  const stop = useCallback(() => {
    if (!recording) return;
    try { mediaRef.current?.stop(); } catch { /* noop */ }
  }, [recording]);

  const idleLabel = snippet?.text || "Record";
  const activeLabel = `${Math.max(1, elapsed)}s · Tap to stop`;

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled || !supported}
      data-testid={testid || "voice-recorder-btn"}
      aria-pressed={recording}
      className={[
        "flex flex-col items-center justify-center gap-2 rounded-3xl border-2 p-6",
        "min-h-[128px] min-w-[128px] transition-all active:scale-[0.97]",
        "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-red-300",
        disabled || !supported
          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
          : recording
            ? "border-red-600 bg-red-600 text-white animate-pulse"
            : "border-red-500 bg-red-50 text-red-800 hover:bg-red-100",
      ].join(" ")}
    >
      <span className="rounded-full bg-white/20 p-3">
        {!supported ? <MicOff className="w-8 h-8" strokeWidth={2.4} />
          : recording ? <Square className="w-8 h-8" strokeWidth={3} fill="currentColor" />
          : <Mic className="w-8 h-8" strokeWidth={2.4} />}
      </span>
      <span
        data-testid={testid ? `${testid}-label` : "voice-recorder-label"}
        className="text-[14px] font-semibold leading-tight text-center"
        lang={snippet?.locale || "bn-IN"}
      >
        {!supported ? "Mic unavailable" : recording ? activeLabel : idleLabel}
      </span>
    </button>
  );
}

export default VoiceRecorderButton;
